import { useEffect, useRef, useState } from 'react';
import { cn } from '@nexa/ui';

export interface PmTabsProps {
  readonly labels: readonly string[];
  readonly active: number;
  readonly onChange: (index: number) => void;
  /** Tiền tố id, dùng để nối `aria-controls` của tab với `id` của panel. */
  readonly idPrefix: string;
}

/**
 * Bộ 3 tab của `#pm` theo APG "Tabs with Automatic Activation":
 *   - `role="tablist"` / `role="tab"` + `aria-selected` + `aria-controls`
 *   - roving tabindex: chỉ tab đang chọn có `tabIndex 0`, nên Tab qua bộ tab là MỘT điểm dừng
 *     chứ không phải ba
 *   - mũi tên trái/phải đổi tab VÀ mang focus theo, quay vòng ở hai đầu
 *
 * Hình thức: tab đang chọn nền `brand-600`, chữ trắng — ĐO từ `template.html:845` và hàm
 * `setPm()`. (`reference.md` §3 ghi "nền surface + shadow-hairline" là SAI; plan lô A′ §0.3
 * mục 1 sửa lại, Task 15 chép vào skill.)
 *
 * Dưới `sm` cho cuộn ngang thay vì để wrap thành 2 hàng (`reference.md` §2.2).
 *
 * HAI TẦNG, và việc tách chúng ra là bắt buộc chứ không phải sở thích: tầng ngoài cuộn ngang,
 * tầng trong (`role="tablist"`) là phần tử NEO của pill. Một phần tử con định vị tuyệt đối vẫn
 * đóng góp vào `scrollWidth` của hộp cuộn chứa nó, nên neo pill vào chính hộp cuộn sinh ra thanh
 * cuộn ngang ở chỗ trước đó không có ⇒ `#pm` cao thêm đúng chiều cao thanh cuộn — CHỈ trên nền
 * tảng mà thanh cuộn chiếm chỗ (Windows/Linux). macOS vẽ thanh cuộn phủ lên nội dung, nên đo trên
 * máy phát triển sẽ báo an toàn cho một thứ không an toàn. Chi tiết ở `motion.css`, mục
 * `.nx-tabpill`; cổng ở `PmTabs.test.tsx`, ba ca cuối.
 *
 * `role="tablist"` PHẢI ở tầng trong: để nó ở hộp cuộn thì có một div thường chen giữa tablist và
 * các tab, phá quan hệ owned-elements của ARIA.
 */
export function PmTabs({ labels, active, onChange, idPrefix }: PmTabsProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const [pill, setPill] = useState({ x: 0, width: 0 });

  const move = (delta: number) => {
    const next = (active + delta + labels.length) % labels.length;
    onChange(next);
    refs.current[next]?.focus();
  };

  /*
   * Pill lấy hình dạng từ chính nút đang chọn, nên nó không bao giờ vượt khỏi phần tử neo.
   *
   * ĐO MỘT LẦN LÚC MOUNT LÀ SAI, và ba phép đo dưới đây là ba lý do khác nhau:
   *   - `measure()` ngay: cho lần đổi tab, khi layout đã ổn định;
   *   - sau `document.fonts.ready`: bề rộng nút phụ thuộc chữ đã render bằng ĐÚNG font. Đo trước
   *     khi font tải xong thì lệch, và `document.fonts.status` không nói thật về việc font đã
   *     được ÁP hay chưa — nên chờ promise chứ không hỏi trạng thái;
   *   - `ResizeObserver`: hộp cuộn co giãn theo khổ màn hình, mà nút cũng đổi bậc `min-height`
   *     theo breakpoint — cả hai xảy ra được mà không có sự kiện resize nào của window.
   */
  useEffect(() => {
    const node = refs.current[active];
    if (node === null || node === undefined) return;

    const measure = () => {
      const el = refs.current[active];
      if (el === null || el === undefined) return;
      const x = el.offsetLeft;
      const width = el.offsetWidth;
      // Trả về `prev` để React bỏ qua lần render lại: ba phép đo thường ra cùng một số.
      setPill((prev) => (prev.x === x && prev.width === width ? prev : { x, width }));
    };

    measure();
    void document.fonts.ready.then(measure);

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [active]);

  return (
    <div className="flex max-w-full overflow-x-auto rounded-pill border border-ink-100 bg-surface-muted p-[5px]">
      <div role="tablist" className="relative flex gap-2">
        <span
          aria-hidden="true"
          className="nx-tabpill absolute inset-y-0 left-0 rounded-pill bg-brand-600"
          style={{ width: pill.width, transform: `translateX(${String(pill.x)}px)` }}
        />
        {labels.map((label, index) => (
          <button
            key={label}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${String(index)}`}
            aria-selected={active === index}
            aria-controls={`${idPrefix}-view-${String(index)}`}
            tabIndex={active === index ? 0 : -1}
            onClick={() => {
              onChange(index);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                move(1);
              }
              if (event.key === 'ArrowLeft') {
                event.preventDefault();
                move(-1);
              }
            }}
            className={cn(
              'relative z-10 min-h-11 flex-none rounded-pill px-5 text-body-sm font-semibold transition [transition-duration:var(--duration-hover)] md:min-h-[38px]',
              active === index
                ? /*
                   * Nền của tab đang chọn do pill đảm nhận — nhưng CHỈ khi pill đã đo được. Trong
                   * HTML tĩnh (SSG) và trước khi hydrate, `width` còn là 0 nên pill vô hình; bỏ
                   * nền ở đây một cách vô điều kiện là để chữ trắng nằm trên nền sáng, tức nhãn
                   * tab đang chọn KHÔNG ĐỌC ĐƯỢC ở mọi lần tải nguội. Giữ nền làm lưới đỡ cho
                   * trạng thái chưa-đo, và bỏ nó ngay khi pill có bề rộng thật — hai trạng thái
                   * trông y nhau nên không có nháy.
                   */
                  cn('text-white', pill.width === 0 && 'bg-brand-600')
                : 'text-ink-700 hover:text-brand-600',
            )}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
